import React from "react";
import { Outlet, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Activity, LayoutDashboard, CalendarDays, Inbox, Users, LogOut, CalendarCheck, Send, MessageSquare, Heart, Trash2, Bot } from "lucide-react";
import NotificationBell from "@/components/NotificationBell";

const adminNav = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard" },
  { to: "/schedule", label: "Schedule", icon: CalendarDays, testid: "nav-schedule" },
  { to: "/assistant", label: "Assistant", icon: Bot, testid: "nav-assistant" },
  { to: "/requests", label: "Requests", icon: Inbox, testid: "nav-requests" },
  { to: "/staff", label: "Staff", icon: Users, testid: "nav-staff" },
  { to: "/comms", label: "Comms", icon: MessageSquare, testid: "nav-comms" },
  { to: "/donate", label: "Donate", icon: Heart, testid: "nav-donate" },
];
const techNav = [
  { to: "/my-schedule", label: "My Shifts", icon: CalendarCheck, testid: "nav-my-schedule" },
  { to: "/my-requests", label: "My Requests", icon: Send, testid: "nav-my-requests" },
  { to: "/comms", label: "Comms", icon: MessageSquare, testid: "nav-comms" },
  { to: "/donate", label: "Donate", icon: Heart, testid: "nav-donate" },
];

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const nav = user?.role === "admin" ? adminNav : techNav;

  const doLogout = async () => { await logout(); navigate("/login"); };

  const deleteAccount = async () => {
    if (!window.confirm("Delete your account? This permanently removes your profile, shifts, requests and messages. This cannot be undone.")) return;
    if (!window.confirm("Are you absolutely sure? This is your final confirmation.")) return;
    try {
      await api.delete("/me");
      toast.success("Your account has been deleted.");
      await logout();
      navigate("/login");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="rs-grid-bg rs-scanlines min-h-screen text-white flex flex-col md:flex-row">
      {/* Sidebar */}
      <aside className="md:w-64 md:min-h-screen border-b md:border-b-0 md:border-r border-[#D902EE]/20 bg-[#0F1219] flex md:flex-col z-10 relative">
        <div className="p-5 flex items-center justify-between gap-2 border-b border-slate-800/60 md:w-full">
          <div className="flex items-center gap-2">
            <Activity className="w-6 h-6 text-[#D902EE] rs-text-glow-magenta" />
            <span className="font-mono-rs text-lg font-bold tracking-tighter uppercase hidden md:inline">
              Radio<span className="text-[#D902EE]">Shift</span>
            </span>
          </div>
          <NotificationBell />
        </div>

        <nav className="flex md:flex-col flex-1 md:p-3 overflow-x-auto">
          {nav.map((n) => (
            <NavLink
              key={n.to} to={n.to} data-testid={n.testid}
              className={({ isActive }) =>
                `flex items-center gap-3 px-4 py-3 text-sm font-medium tracking-wide transition-all duration-300 whitespace-nowrap md:mb-1 border-l-2 ${
                  isActive
                    ? "text-white border-[#D902EE] bg-[#D902EE]/10 rs-text-glow-magenta"
                    : "text-slate-400 border-transparent hover:text-white hover:bg-[#171A21]"
                }`
              }
            >
              <n.icon className="w-4 h-4" />
              <span>{n.label}</span>
            </NavLink>
          ))}
        </nav>

        <div className="p-3 border-t border-slate-800/60 hidden md:block">
          <div className="px-2 mb-3">
            <div className="text-sm font-medium truncate">{user?.name}</div>
            <div className="text-[10px] tracking-[0.2em] uppercase text-[#00F0FF] font-mono-rs">
              {user?.role === "admin" ? "Administrator" : `${user?.category} Tech`}
            </div>
          </div>
          <button
            data-testid="logout-button" onClick={doLogout}
            className="w-full flex items-center gap-2 px-3 py-2 text-sm text-slate-400 border border-slate-700 hover:border-[#FF003C] hover:text-[#FF003C] transition-all duration-300"
          >
            <LogOut className="w-4 h-4" /> Sign Out
          </button>
          {user?.role === "tech" && (
            <button
              data-testid="delete-account-button" onClick={deleteAccount}
              className="w-full flex items-center gap-2 px-3 py-2 mt-2 text-xs text-slate-500 border border-transparent hover:border-[#FF003C]/60 hover:text-[#FF003C] transition-all duration-300"
            >
              <Trash2 className="w-3.5 h-3.5" /> Delete Account
            </button>
          )}
          <NavLink data-testid="nav-privacy" to="/privacy" className="block text-center mt-3 text-[10px] tracking-[0.15em] uppercase text-slate-600 hover:text-[#00F0FF] font-mono-rs transition-colors">
            Privacy Policy
          </NavLink>
          <NavLink data-testid="nav-support" to="/support" className="block text-center mt-2 text-[10px] tracking-[0.15em] uppercase text-slate-600 hover:text-[#00F0FF] font-mono-rs transition-colors">
            Support
          </NavLink>
          <div data-testid="sidebar-company" className="text-center mt-4 text-[9px] tracking-[0.15em] uppercase text-slate-700 font-mono-rs">
            Built by GSoftware LLC
          </div>
        </div>

        <button
          data-testid="logout-button-mobile" onClick={doLogout}
          className="md:hidden flex items-center px-4 text-slate-400 hover:text-[#FF003C]"
        >
          <LogOut className="w-5 h-5" />
        </button>
      </aside>

      <main className="flex-1 relative z-[2] p-4 sm:p-6 lg:p-8 overflow-x-hidden">
        <Outlet />
      </main>
    </div>
  );
}
